import type { StaffAnalyticsOverview } from "@lafayette/schemas";

import { bucketText } from "../format";
import { activityTotals } from "../overview-model";
import { barLayout, gridValues, niceMax } from "./chart-math";

// "Activity over time" (Paper): bars of the customer's messages per bucket of time, with a dot above the bar for each request, payment and trip of
// that bucket. SVG, 1 px grid lines, palette colours, no gradient. The chart is decoration (`aria-hidden`): every number is also written as text,
// the totals under the chart and a table of every bucket that a person can open.

type Activity = StaffAnalyticsOverview["records"]["activity"];
type Point = Activity["points"][number];

const WIDTH = 640;
const HEIGHT = 180;
const MARKER_R = 4;

const MARKERS = [
  { key: "requests", label: "Request", className: "fill-brass" },
  { key: "payments", label: "Payment", className: "fill-success" },
  { key: "trips", label: "Trip", className: "fill-info" },
] as const;

// Whole class names, written out: Tailwind finds a class only when it is in the source in full.
const ACTIVITY_LEGEND = [
  { label: "Messages from the customer", className: "bg-accent border border-info-border" },
  { label: "Request", className: "bg-brass" },
  { label: "Payment", className: "bg-success" },
  { label: "Trip", className: "bg-info" },
] as const;

function markerCount(point: Point): number {
  return MARKERS.filter((marker) => point[marker.key] > 0).length;
}

function Marks({ point, x, y }: Readonly<{ point: Point; x: number; y: number }>) {
  const present = MARKERS.filter((marker) => point[marker.key] > 0);
  return (
    <>
      {present.map((marker, index) => (
        <circle
          key={marker.key}
          cx={x}
          cy={y - index * (MARKER_R * 2 + 3)}
          r={MARKER_R}
          className={`${marker.className} stroke-card`}
          strokeWidth={2}
        />
      ))}
    </>
  );
}

export function ActivityChart({ activity, tz }: Readonly<{ activity: Activity; tz: string }>) {
  const { points, interval } = activity;
  const max = niceMax(Math.max(...points.map((point) => point.customerMessages), 0));
  const markerRoom = MARKERS.length * (MARKER_R * 2 + 3) + 4;
  const bars = barLayout(
    points.map((point) => point.customerMessages),
    { width: WIDTH, height: HEIGHT - markerRoom },
    max,
  );
  const first = points[0];
  const last = points.at(-1);
  const totals = activityTotals(points);
  return (
    <div className="grid gap-3">
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox={`0 0 ${WIDTH + 36} ${HEIGHT + 22}`}
        className="h-auto w-full"
      >
        <g transform={`translate(36 ${markerRoom})`}>
          {gridValues(max).map((value) => {
            const y = HEIGHT - markerRoom - (value / max) * (HEIGHT - markerRoom);
            return (
              <g key={value}>
                <line
                  x1={0}
                  x2={WIDTH}
                  y1={y}
                  y2={y}
                  className="stroke-border"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={-8}
                  y={y + 4}
                  textAnchor="end"
                  className="fill-muted-foreground text-[11px]"
                >
                  {Math.round(value)}
                </text>
              </g>
            );
          })}
          {bars.map((bar, index) => {
            const point = points[index] as Point;
            return (
              <g key={point.t}>
                <rect
                  x={bar.x}
                  y={bar.y}
                  width={bar.width}
                  height={bar.height}
                  rx={Math.min(6, bar.width / 2)}
                  className="fill-accent"
                />
                {markerCount(point) > 0 ? (
                  <Marks point={point} x={bar.x + bar.width / 2} y={bar.y - MARKER_R - 4} />
                ) : null}
              </g>
            );
          })}
          {first === undefined || last === undefined ? null : (
            <>
              <text
                x={0}
                y={HEIGHT - markerRoom + 16}
                className="fill-muted-foreground text-[11px]"
              >
                {bucketText(first.t, tz, interval)}
              </text>
              <text
                x={WIDTH}
                y={HEIGHT - markerRoom + 16}
                textAnchor="end"
                className="fill-muted-foreground text-[11px]"
              >
                {bucketText(last.t, tz, interval)}
              </text>
            </>
          )}
        </g>
      </svg>
      <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs text-muted-foreground">
        {ACTIVITY_LEGEND.map((item) => (
          <li key={item.label} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className={`inline-block size-2.5 rounded-full ${item.className}`}
            />
            {item.label}
          </li>
        ))}
      </ul>
      <p className="m-0 text-sm leading-6 text-foreground">
        In this period: {totals.customerMessages} messages from the customer, {totals.requests}{" "}
        requests, {totals.payments} payments and {totals.trips} trips.
      </p>
      <details className="text-sm">
        <summary className="inline-flex min-h-11 cursor-pointer items-center font-medium text-link compact:min-h-8">
          Show the numbers of each {interval}
        </summary>
        <ul className="m-0 grid max-h-64 list-none gap-0.5 overflow-auto p-0">
          {points.map((point) => (
            <li key={point.t} className="flex flex-wrap gap-x-3 border-t border-hairline py-1">
              <span className="min-w-20 font-medium">{bucketText(point.t, tz, interval)}</span>
              <span>{point.customerMessages} messages</span>
              <span>{point.requests} requests</span>
              <span>{point.payments} payments</span>
              <span>{point.trips} trips</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
